import { useTranslation } from "@i18n";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { AccordionDetails, AccordionSummary, Chip } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import IconComponent from "@CubeComponents/icons";
import Accordion from "@CubeComponents/html/accordion/materialUiAccordian/index";
import { contractsTypeHeading } from "@Features/commercial-shared/contracts/constant";
import type { GetItemBreakUpDetailsResponse } from "@ServerTypes/contracts/contracts.schema";

type ContractRow = NonNullable<GetItemBreakUpDetailsResponse["contracts"]>[number];
type ContractForm = NonNullable<GetItemBreakUpDetailsResponse["contractForms"]>[number];
type Forms = NonNullable<ContractForm["forms"]>[number];
interface ItemBreakUpModalProps {
  source: string;
  itemDetails?: {
    contracts?: ContractRow[];
    contractForms?: ContractForm[];
  };
  closeModalCallBack: () => void;
}
function ItemBreakUpModal({
  source,
  itemDetails = {
    contractForms: [],
    contracts: [],
  },
  closeModalCallBack,
}: ItemBreakUpModalProps) {
  const { t } = useTranslation();
  // Truncate (do not round) to at most three decimal places.
  const truncateToThreeDecimals = (value: number | undefined) => {
    if (typeof value !== "number" || Number.isNaN(value)) return value;
    return (Math.trunc(value * 1000) / 1000).toString();
  };
  const breakUpHeader = () => (
    <div className="flex justify-between w-full items-center">
      {`${source == "wbsItem" ? "Amount" : "Quantity"} Breakdown`}
    </div>
  );

  const contractTableItems = [
    t("Contract Name"),
    t("Contract Type"),
    t("Contract Status"),
    `${source == "wbsItem" ? t("Assigned Amount") : t("Assigned Quantity")}`,
  ];

  const formTableItems = [
    {
      name: t("Form No"),
      icon: "NumericIcon",
    },
    {
      name: t("Form Name"),
      icon: "SingleLineTextBox",
    },
    {
      name: `${source == "wbsItem" ? t("Assigned Amount") : t("Assigned Quantity")}`,
      icon: "NumericIcon",
    },
  ];
  const getContractTable = (data: ContractRow[] = []) => {
    if (data.length == 0) {
      return "";
    }
    let totalAssignedInContracts: number =
      data?.reduce((accumulator: number, object: ContractRow) => accumulator + object.assignedEntity!, 0) || 0;
    return (
      <table className="w-full h-full">
        <tr className="border border-collapse border-button_light bg-light_background">
          {contractTableItems.map((name: string) => (
            <th key={name} className="p-2 text-start border-e border-button_light">
              {name}
            </th>
          ))}
        </tr>
        {data.length ? (
          data.map((item: ContractRow) => (
            <tr key={item._id} className="border border-collapse border-button_light p-3">
              <td className="px-3 py-3 ps-2 pe-2 border-e border-button_light">{item.contractName}</td>
              <td className="px-3 py-3 ps-2 pe-2 border-e border-button_light">
                {contractsTypeHeading[item.contractType!]}
              </td>
              <td className="px-3 py-3 ps-2 pe-2 border-e border-button_light">
                {item.lockData!.status ? "Locked" : "Un-Locked"}
              </td>
              <td className="px-3 py-3 ps-2 pe-2">{truncateToThreeDecimals(item.assignedEntity)}</td>
            </tr>
          ))
        ) : (
          <tr className="border border-collapse border-button_light p-3">
            {contractTableItems.map((name: string) => (
              <th key={name} className="px-3 py-3 ps-2 pe-2 border-e border-button_light">
                -
              </th>
            ))}
          </tr>
        )}
        <tr className="border border-collapse border-button_light p-3 bg-light_background">
          <td className="p-2 font-semibold">{t("Total Assigned")}</td>
          <td className="p-2" />
          <td className="p-2" />
          <td className="p-2 font-semibold">{truncateToThreeDecimals(totalAssignedInContracts)}</td>
        </tr>
      </table>
    );
  };
  const generateArrowIcons = (n: number) => {
    if (n == 0) {
      return <></>;
    }
    const array = Array.from({ length: n }, (_, index) => index);
    return (
      <div className="flex gap-1 items-center">
        {array.map(() => (
          <IconComponent key="" name="childData" width="1.5rem" height="1.5rem" />
        ))}
      </div>
    );
  };

  const getFormTable = (data: Forms[] = []) => {
    let totalAssignedInForms: number =
      data?.reduce((accumulator: number, object: Forms) => accumulator + object.assignedEntity!, 0) || 0;
    return (
      <table className="w-full h-full">
        <tr className="border border-collapse border-button_light bg-light_background">
          {formTableItems.map((item: { name: string; icon: string }) => (
            <th key={item.name} className="p-2 text-start border-e border-button_light">
              <div className="flex gap-2 items-center">
                <IconComponent name={item.icon} width="1rem" height="1rem" />
                {item.name}
              </div>
            </th>
          ))}
        </tr>
        {data.length ? (
          data.map((item: Forms) => (
            <tr key={item._id} className="border border-collapse border-button_light p-3">
              <td className="px-3 py-3 ps-2 pe-2 border-e border-button_light">{item.formNo}</td>
              <td className="px-3 py-3 ps-2 pe-2 border-e border-button_light">{item.formTitle}</td>
              <td className="px-3 py-3 ps-2 pe-2">{truncateToThreeDecimals(item.assignedEntity)}</td>
            </tr>
          ))
        ) : (
          <tr className="border border-collapse border-button_light p-3">
            {contractTableItems.map((name: string) => (
              <th key={name} className="px-3 py-3 ps-2 pe-2 border-e border-button_light">
                -
              </th>
            ))}
          </tr>
        )}
        <tr className="border border-collapse border-button_light p-3 bg-light_background">
          <td className="p-2 font-semibold">{`${data.length} rows`}</td>
          <td className="p-2" />
          {/* <td className="p-2 font-semibold">{t("Total Assigned")}</td> */}
          <td className="p-2 font-semibold">{`Total:  ${truncateToThreeDecimals(totalAssignedInForms)}`}</td>
        </tr>
      </table>
    );
  };
  const getContractHeader = (data: ContractForm) => {
    return (
      <div className="flex  w-full py-3 ps-3 pe-3 items-center gap-3">
        {generateArrowIcons(data.contractLevel!)}
        <span className="font-semibold">{data.contractName}</span>
        <Chip
          size="small"
          sx={{
            fontWeight: 400,
            background: "#6BBBE3",
            color: "white",
          }}
          label={contractsTypeHeading[data.contractType!]}
        />
      </div>
    );
  };
  const breakUpContent = () => {
    const sortedData = itemDetails.contractForms?.sort((a, b) => a.contractLevel! - b.contractLevel!) || [];
    return (
      <div className="flex flex-col gap-2 w-full">
        {getContractTable(itemDetails.contracts)}
        {sortedData?.map((contract: ContractForm) => (
          <Accordion
            defaultExpanded
            className="w-full pe-4"
            border={"1px solid var(--primary-blue)"}
            borderRadius="0.625rem"
            key={contract._id}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>{getContractHeader(contract)}</AccordionSummary>
            <AccordionDetails>{getFormTable(contract.forms)}</AccordionDetails>
          </Accordion>
        ))}
      </div>
    );
  };
  return (
    <ModalStateDialogComponent
      footerContent={<></>}
      headerTitle={breakUpHeader()}
      modelContent={breakUpContent()}
      allowModalPadding={false}
      onCancel={closeModalCallBack}
      isVisible
      width="60%"
      height="80%"
    />
  );
}
export default ItemBreakUpModal;
